import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ReportsService } from '../../services/reports.service';

/**
 * Recurring Replenishment — the counter/route worklist for the auto-replenish
 * program, plus the two views used to design it.
 *
 *   Due Now    every (customer, item) whose next reorder date has arrived. This
 *              is what a rep or a counter person works through.
 *   Customers  who is predictable enough to enrol, ranked by recurring spend.
 *   Items      which SKUs the program should launch on, and the annual units
 *              a full enrolment would imply.
 *
 * All three read the nightly `recurring_candidates` snapshot. A pair is graded
 * on cv = stddev(gap)/mean(gap): A is a tight cycle, B is a real but looser one,
 * C is a repeat you would not promise a date on. Anything more than a full cycle
 * past due is "lapsed" — that customer stopped rather than slipped, and the
 * server keeps them off the due list so they cannot squat at the top of a
 * days-late sort forever.
 */
@Component({
	selector: 'app-recurring-replenishment',
	templateUrl: './recurring-replenishment.component.html',
	styleUrls: ['./recurring-replenishment.component.scss']
})
export class RecurringReplenishmentComponent implements OnInit {
	@ViewChild('historyDialog', { static: true }) historyDialog!: TemplateRef<any>;

	filterForm: UntypedFormGroup;

	view: 'due' | 'customers' | 'items' = 'due';
	loading = false;
	errorMessage = '';

	sort = '';
	dir: 'asc' | 'desc' = 'desc';

	// Product line by default. Per-SKU splits one habit across dozens of shades
	// and makes a weekly colour buyer look like an irregular monthly one.
	level: 'line' | 'sku' = 'line';

	// Which line row is expanded, and the SKUs behind it.
	expandedCode = '';
	skuRows: any[] = [];
	skusLoading = false;

	rows: any[] = [];
	totals: any = null;
	snapshot: any = null;
	truncated = false;
	limit = 500;

	salesmen: any[] = [];
	branches: any[] = [];
	cadences: string[] = [];

	// Drill-in basket for one customer, shown under the grid.
	selected: any = null;
	detailRows: any[] = [];
	detailSummary: any = null;
	detailLoading = false;

	// "Why this number" — the orders behind one item's cycle.
	history: any = null;
	historyLoading = false;

	constructor(
		private fb: UntypedFormBuilder,
		private reportsService: ReportsService,
		private dialog: MatDialog,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.filterForm = this.fb.group({
			grade: ['AB'],
			horizon: [7],
			salesman: [''],
			branch: [''],
			cadence: [''],
			search: [''],
		});

		this.reportsService.getRecurringFilters().subscribe((r: any) => {
			if (!r?.success) { return; }
			this.salesmen = r.salesmen || [];
			this.branches = r.branches || [];
			this.cadences = r.cadences || [];
		});

		this.load();
	}

	load(): void {
		this.loading = true;
		this.errorMessage = '';
		this.selected = null;

		const f = this.filterForm.value;
		const params: any = {
			grade: f.grade,
			salesman: f.salesman || '',
			branch: f.branch || '',
			cadence: f.cadence || '',
			search: f.search || '',
			sort: this.sort,
			dir: this.dir,
			level: this.level,
		};
		if (this.view === 'due') {
			params.horizon = f.horizon;
		}

		const call = this.view === 'due' ? this.reportsService.getRecurringDue(params)
			: this.view === 'customers' ? this.reportsService.getRecurringCustomers(params)
				: this.reportsService.getRecurringItems(params);

		call.subscribe((r: any) => {
			this.loading = false;
			if (!r?.success) {
				this.rows = [];
				this.totals = null;
				this.errorMessage = r?.message || 'Could not load the recurring snapshot.';
				return;
			}
			this.rows = r.rows || [];
			this.totals = r.totals || null;
			this.snapshot = r.snapshot || null;
			this.truncated = !!r.truncated;
			this.limit = r.limit || this.limit;
		});
	}

	setView(v: 'due' | 'customers' | 'items'): void {
		if (this.view === v) { return; }
		this.view = v;
		// Sort keys differ per view; carrying one over would silently fall back
		// to the default anyway, so reset it rather than show a stale arrow.
		this.sort = '';
		this.dir = 'desc';
		this.load();
	}

	openCustomer(row: any): void {
		if (this.selected && this.selected.debtorno === row.debtorno) {
			this.selected = null;
			return;
		}
		this.selected = row;
		this.detailRows = [];
		this.detailSummary = null;
		this.detailLoading = true;

		this.reportsService.getRecurringCustomer(row.debtorno).subscribe((r: any) => {
			this.detailLoading = false;
			if (!r?.success) { return; }
			this.detailRows = r.rows || [];
			this.detailSummary = r.summary || null;
		});
	}

	goToCustomer(debtorno: string): void {
		this.router.navigate(['/customers/view', debtorno]);
	}

	/**
	 * Show the orders and invoices the cycle was measured from. The gap column in
	 * that dialog is literally the series the cadence averages, so an operator can
	 * check the number rather than trust it.
	 */
	openHistory(debtorno: string, code: string): void {
		this.history = null;
		this.historyLoading = true;
		this.dialog.open(this.historyDialog, { width: '760px', maxWidth: '95vw' });

		this.reportsService.getRecurringHistory(debtorno, code, this.level).subscribe((r: any) => {
			this.historyLoading = false;
			this.history = r?.success ? r : null;
		});
	}

	/**
	 * History for one SKU inside an expanded line row. Forces level 'sku' — the
	 * grid is on 'line', and passing that would look up a lineid that doesn't
	 * exist and come back empty.
	 */
	openSkuHistory(debtorno: string, stkcode: string): void {
		this.history = null;
		this.historyLoading = true;
		this.dialog.open(this.historyDialog, { width: '760px', maxWidth: '95vw' });

		this.reportsService.getRecurringHistory(debtorno, stkcode, 'sku').subscribe((r: any) => {
			this.historyLoading = false;
			this.history = r?.success ? r : null;
		});
	}

	setLevel(l: 'line' | 'sku'): void {
		if (this.level === l) { return; }
		this.level = l;
		this.expandedCode = '';
		this.load();
	}

	/** Expand a line row to show which SKUs of it the customer buys. */
	toggleSkus(row: any): void {
		const id = row.debtorno + '|' + row.code;
		if (this.expandedCode === id) {
			this.expandedCode = '';
			return;
		}
		this.expandedCode = id;
		this.skuRows = [];
		this.skusLoading = true;

		this.reportsService.getRecurringSkus(row.debtorno, row.code).subscribe((r: any) => {
			this.skusLoading = false;
			this.skuRows = r?.success ? (r.rows || []) : [];
		});
	}

	isExpanded(row: any): boolean {
		return this.expandedCode === row.debtorno + '|' + row.code;
	}

	/**
	 * The A/B/C grade said nothing to anyone who hadn't read the spec. Say how
	 * much to trust the cycle in words, folded into the sentence that states it.
	 */
	buyingPattern(r: any): string {
		const every = 'every ' + r.cycle_days + ' days';
		const gaps = Number(r.buys) - 1;
		const spread = Number(r.max_gap) / Math.max(1, Number(r.min_gap));

		// "Like clockwork" has to earn it. The stored grade is a coefficient of
		// variation, and over only 3 gaps that number lies — 50/71/98 days scores
		// cv 0.33 (grade A) despite the longest wait being double the shortest.
		// So require 5+ gaps before the cv means anything, plus a loose spread
		// guard for the pathological case. Takes grade A from 11,523 rows to
		// 2,441 that can actually be called regular.
		if (r.grade === 'A' && gaps >= 5 && spread < 2.5) {
			return 'Like clockwork, ' + every;
		}
		if (r.grade === 'A' || r.grade === 'B') {
			return 'Usually ' + every;
		}
		return 'Roughly ' + every;
	}

	/** Days since their last purchase, from the date — never from a field that may be absent. */
	private daysSinceLast(r: any): number {
		const last = new Date(r.last_buy);
		if (isNaN(last.getTime())) { return NaN; }
		return Math.round((Date.now() - last.getTime()) / 86400000);
	}

	/**
	 * The whole justification in one line: they buy on this rhythm, they last
	 * bought then, so they are this far past it. This is the answer to "why do we
	 * think they're due" — it belongs on the row, not behind a drill-down.
	 */
	whyDue(r: any): string {
		const since = this.daysSinceLast(r);
		const last = isNaN(since) ? 'Last bought ' + r.last_buy
			: since === 0 ? 'Last bought today'
				: since === 1 ? 'Last bought yesterday'
					: 'Last bought ' + since + ' days ago';
		const late = Number(r.days_late);

		if (late > 0) { return last + ' — ' + late + ' days past due'; }
		if (late === 0) { return last + ' — due today'; }
		return last + ' — due in ' + Math.abs(late) + ' days';
	}

	/** Band keys are storage values, not something to show a person. */
	cadenceLabel(c: string): string {
		switch (c) {
		case 'weekly': return 'Weekly';
		case '2weeks': return 'Every 2 weeks';
		case '3weeks': return 'Every 3 weeks';
		case 'monthly': return 'Monthly';
		case '6weeks': return 'Every 6 weeks';
		case '2months': return 'Every 2 months';
		case '3months': return 'Every 3 months';
		case 'longer': return 'Less often';
		default: return c;
		}
	}

	/**
	 * Column sort. Server-side, because the grid is capped at 500 rows — sorting
	 * only what happens to be on screen would quietly reorder a slice rather than
	 * the whole result.
	 */
	sortBy(key: string): void {
		if (this.sort === key) {
			this.dir = this.dir === 'desc' ? 'asc' : 'desc';
		} else {
			this.sort = key;
			this.dir = 'desc';
		}
		this.load();
	}

	sortIcon(key: string): string {
		if (this.sort !== key) { return ''; }
		return this.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	lateLabel(days: number): string {
		const d = Number(days);
		if (d > 0) { return d + ' days late'; }
		if (d === 0) { return 'due today'; }
		return 'in ' + Math.abs(d) + ' days';
	}

	exportCsv(): void {
		if (!this.rows.length) { return; }

		const keys = Object.keys(this.rows[0]);
		const lines = [keys.join(',')];
		for (const r of this.rows) {
			lines.push(keys.map(k => {
				const v = r[k];
				return (v === null || v === undefined) ? '' : JSON.stringify(String(v));
			}).join(','));
		}

		const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'recurring-' + this.view + '-' + new Date().toISOString().substring(0, 10) + '.csv';
		a.click();
		URL.revokeObjectURL(url);
	}
}
